import { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Upload, FileText, AlertCircle, CheckCircle2, Loader2 } from 'lucide-react';
import { aiPurchasesAPI } from '../../api';
import Layout from '../../components/Layout';
import { Button } from '../../components/ui/Button';
import { Card, CardContent } from '../../components/ui/Card';
import { useToast } from '../../hooks/useToast';

const PurchaseAIUpload = () => {
  const navigate = useNavigate();
  const { success, error, ToastContainer } = useToast();
  const fileInputRef = useRef(null);

  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');

  const handleFileSelect = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      // Validate file type
      const allowedTypes = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
      const allowedExtensions = ['.pdf', '.jpg', '.jpeg', '.png', '.gif', '.webp'];
      const fileExt = '.' + selectedFile.name.split('.').pop().toLowerCase();

      if (!allowedTypes.includes(selectedFile.type) && !allowedExtensions.includes(fileExt)) {
        setErrorMessage('Only PDF and image files are allowed');
        setFile(null);
        return;
      }

      // Validate file size (10MB max)
      if (selectedFile.size > 10 * 1024 * 1024) {
        setErrorMessage('File size must be less than 10MB');
        setFile(null);
        return;
      }

      setFile(selectedFile);
      setErrorMessage('');
      setUploadResult(null);
    }
  };

  const handleUpload = async () => {
    if (!file) {
      setErrorMessage('Please select a file to upload');
      return;
    }

    setUploading(true);
    setErrorMessage('');
    setUploadResult(null);

    try {
      const result = await aiPurchasesAPI.upload(file);
      setUploadResult(result);
      success('File uploaded and processed successfully!');
      
      // Navigate to review page after successful upload
      setTimeout(() => {
        navigate('/tenant/purchases/ai-review');
      }, 2000);
    } catch (err) {
      const errorMsg = err.response?.data?.error || err.message || 'Upload failed';
      setErrorMessage(errorMsg);
      error(errorMsg);
    } finally {
      setUploading(false);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();

    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile) {
      const event = {
        target: {
          files: [droppedFile],
        },
      };
      handleFileSelect(event);
    }
  };

  const handleFileInputClick = () => {
    fileInputRef.current?.click();
  };

  return (
    <Layout>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-6">
          <h1 className="text-h1 font-medium text-[var(--ds-text-primary)] mb-2">
            AI Purchase Invoice Upload
          </h1>
          <p className="text-body text-[var(--ds-text-secondary)]">
            Upload a purchase invoice (PDF or image) for AI-powered processing
          </p>
        </div>

        {uploadResult ? (
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <CheckCircle2 className="w-6 h-6 text-green-500" />
                <h2 className="text-h2 font-medium text-[var(--ds-text-primary)]">
                  Upload Successful
                </h2>
              </div>
              <p className="text-body text-[var(--ds-text-secondary)] mb-4">
                {uploadResult.message}
              </p>
              <div className="bg-[var(--ds-control-field-bg)] rounded-[var(--ds-radius-md)] p-4">
                <p className="text-meta text-[var(--ds-text-secondary)] mb-1">Draft ID:</p>
                <p className="text-body font-mono text-[var(--ds-text-primary)]">
                  {uploadResult.draft?.id}
                </p>
                <p className="text-meta text-[var(--ds-text-secondary)] mt-3 mb-1">Status:</p>
                <p className="text-body text-[var(--ds-text-primary)] capitalize">
                  {uploadResult.draft?.status}
                </p>
              </div>
              <div className="mt-4 flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => navigate('/tenant/purchases/ai-review')}
                >
                  View All Drafts
                </Button>
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => navigate('/tenant/purchases')}
                >
                  Go to Purchases
                </Button>
              </div>
              <p className="text-meta text-[var(--ds-text-secondary)] mt-4">
                Redirecting to review page...
              </p>
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardContent className="p-6">
              {/* AI Disclaimer */}
              <div className="mb-6 bg-amber-50 dark:bg-amber-900/20 border border-amber-300 dark:border-amber-700 rounded-[var(--ds-radius-md)] p-4">
                <div className="flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-amber-800 dark:text-amber-300 mb-1">
                      AI Experimental Feature
                    </p>
                    <p className="text-sm text-amber-700 dark:text-amber-400">
                      Please check and validate all extracted data carefully. AI processing is experimental and may make mistakes. Always verify amounts, dates, supplier information, and line items before saving.
                    </p>
                  </div>
                </div>
              </div>

              <div
                onDragOver={uploading ? undefined : handleDragOver}
                onDrop={uploading ? undefined : handleDrop}
                onClick={uploading ? undefined : handleFileInputClick}
                className={`border-2 border-dashed border-[var(--ds-border-subtle)] rounded-[var(--ds-radius-md)] p-12 text-center transition-colors ${
                  uploading 
                    ? 'border-[var(--ds-accent-primary)] opacity-75 cursor-not-allowed' 
                    : 'hover:border-[var(--ds-accent-primary)] cursor-pointer'
                }`}
              >
                {uploading ? (
                  <>
                    <Loader2 className="w-12 h-12 mx-auto mb-4 text-[var(--ds-accent-primary)] animate-spin" />
                    <h3 className="text-h3 font-medium text-[var(--ds-text-primary)] mb-2">
                      Uploading & Processing...
                    </h3>
                    <p className="text-body text-[var(--ds-text-secondary)] mb-4">
                      Please wait while we upload and process your invoice
                    </p>
                    <div className="flex items-center justify-center gap-2 text-[var(--ds-text-secondary)]">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      <span className="text-meta">This may take a few moments</span>
                    </div>
                  </>
                ) : (
                  <>
                    <Upload className="w-12 h-12 mx-auto mb-4 text-[var(--ds-text-secondary)]" />
                    <h3 className="text-h3 font-medium text-[var(--ds-text-primary)] mb-2">
                      Drop your invoice here
                    </h3>
                    <p className="text-body text-[var(--ds-text-secondary)] mb-4">
                      or click to browse
                    </p>
                    <input
                      ref={fileInputRef}
                      type="file"
                      id="file-upload"
                      accept=".pdf,.jpg,.jpeg,.png,.gif,.webp"
                      onChange={handleFileSelect}
                      className="hidden"
                      disabled={uploading}
                    />
                    <Button 
                      type="button" 
                      variant="outline"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleFileInputClick();
                      }}
                      disabled={uploading}
                    >
                      Select File
                    </Button>
                    <p className="text-meta text-[var(--ds-text-secondary)] mt-4">
                      Supported formats: PDF, JPG, PNG, GIF, WEBP (max 10MB)
                    </p>
                  </>
                )}
              </div>

              {file && (
                <div className="mt-6 bg-[var(--ds-control-field-bg)] rounded-[var(--ds-radius-md)] p-4">
                  <div className="flex items-center gap-3">
                    <FileText className="w-5 h-5 text-[var(--ds-text-secondary)]" />
                    <div className="flex-1">
                      <p className="text-body font-medium text-[var(--ds-text-primary)]">
                        {file.name}
                      </p>
                      <p className="text-meta text-[var(--ds-text-secondary)]">
                        {(file.size / 1024 / 1024).toFixed(2)} MB
                      </p>
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setFile(null);
                        setErrorMessage('');
                      }}
                    >
                      Remove
                    </Button>
                  </div>
                </div>
              )}

              {errorMessage && (
                <div className="mt-4 flex items-center gap-2 text-[var(--ds-status-danger)]">
                  <AlertCircle className="w-5 h-5" />
                  <p className="text-body">{errorMessage}</p>
                </div>
              )}

              <div className="mt-6 flex gap-3">
                <Button
                  variant="primary"
                  size="sm"
                  onClick={handleUpload}
                  disabled={!file || uploading}
                  className="flex-1"
                >
                  {uploading ? 'Uploading...' : 'Upload & Process'}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => navigate('/tenant/purchases')}
                >
                  Cancel
                </Button>
              </div>

            </CardContent>
          </Card>
        )}
      </div>
      <ToastContainer />
    </Layout>
  );
};

export default PurchaseAIUpload;
